import http from "@/utils/axios";

const ADMIN_TOKEN_KEY = "impersonation_admin_token";
const LOG_ID_KEY = "impersonation_log_id";
const ADMIN_URL_KEY = "impersonation_admin_url";

function getHostname(val) {
  if (!val) return null;
  try {
    return new URL(val).hostname;
  } catch {
    // Bare host like "localhost:3002" — strip port
    return val.replace(/:\d+$/, "");
  }
}

function isTrustedAdminUrl(url) {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    const trustedHosts = [
      process.env.VUE_APP_ADMIN_URL,
      process.env.VUE_APP_DOCTOR_URL,
      process.env.VUE_APP_GATEKEEPER_URL,
    ]
      .map(getHostname)
      .filter(Boolean);
    return trustedHosts.includes(parsed.hostname);
  } catch {
    return false;
  }
}

export { isTrustedAdminUrl };

export default {
  namespaced: true,
  state: {
    isImpersonating: false,
    adminName: null,
    logId: null,
    transition: null,
  },
  getters: {
    getIsImpersonating: (state) => state.isImpersonating,
    getAdminName: (state) => state.adminName,
    getLogId: (state) => state.logId,
    getTransition: (state) => state.transition,
  },
  mutations: {
    setImpersonation(state, { isImpersonating, adminName, logId }) {
      state.isImpersonating = isImpersonating;
      state.adminName = adminName;
      state.logId = logId;
    },
    clearImpersonation(state) {
      state.isImpersonating = false;
      state.adminName = null;
      state.logId = null;
    },
    setTransition(state, data) {
      state.transition = data;
    },
    clearTransition(state) {
      state.transition = null;
    },
  },
  actions: {
    async startImpersonation(_, { targetUserId, targetRole }) {
      const { data } = await http.post("/admin/impersonation/start", {
        target_user_id: targetUserId,
      });

      const currentAdminToken = localStorage.getItem("authtoken");
      localStorage.setItem(ADMIN_TOKEN_KEY, currentAdminToken);
      localStorage.setItem(LOG_ID_KEY, data.impersonation.log_id);
      localStorage.setItem(
        ADMIN_URL_KEY,
        window.location.origin + window.location.pathname
      );
      localStorage.setItem(
        "impersonation_admin_name",
        data.impersonation.admin_name
      );

      let redirectUrl;
      const adminUrl = encodeURIComponent(window.location.href);
      const code = data.exchange_code;
      const adminNameParam = encodeURIComponent(data.impersonation.admin_name);
      if (targetRole === "doctor") {
        redirectUrl = `${window.location.protocol}//${process.env.VUE_APP_DOCTOR_URL}?impersonate_code=${code}&admin_url=${adminUrl}&admin_name=${adminNameParam}`;
      } else {
        const koszykBase = process.env.VUE_APP_CART_URL;
        redirectUrl = `${koszykBase}/cart/?impersonate_code=${code}&admin_url=${adminUrl}&admin_name=${adminNameParam}`;
      }

      return {
        redirectUrl,
        targetUser: data.user,
        adminName: data.impersonation.admin_name,
        targetRole,
      };
    },

    async stopImpersonation({ commit }, { skipRedirect = false } = {}) {
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      const logId = localStorage.getItem(LOG_ID_KEY);
      const adminUrl = localStorage.getItem(ADMIN_URL_KEY);

      const safeLogId = logId && /^\d+$/.test(logId) ? logId : null;
      if (adminToken && safeLogId) {
        try {
          await http.post(
            `/admin/impersonation/${safeLogId}/stop`,
            {},
            {
              headers: { Authorization: "Bearer " + adminToken },
            }
          );
        } catch (e) {
          // Token may already be expired/revoked, continue cleanup
        }
      }

      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(LOG_ID_KEY);
      localStorage.removeItem(ADMIN_URL_KEY);
      localStorage.removeItem("impersonation_admin_name");

      if (adminToken) {
        localStorage.setItem("authtoken", adminToken);
        http.defaults.headers["Authorization"] = "Bearer " + adminToken;
      }

      commit("clearImpersonation");

      if (!skipRedirect) {
        const safeUrl = isTrustedAdminUrl(adminUrl)
          ? adminUrl
          : `${window.location.protocol}//${process.env.VUE_APP_ADMIN_URL}`;
        window.location.href = safeUrl;
      }

      return isTrustedAdminUrl(adminUrl) ? adminUrl : null;
    },

    async cleanupStaleImpersonation({ commit }) {
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      const logId = localStorage.getItem(LOG_ID_KEY);

      if (!adminToken) return;

      const authHeaders = {
        headers: { Authorization: "Bearer " + adminToken },
      };
      try {
        const safeLogId = logId && /^\d+$/.test(logId) ? logId : null;
        if (safeLogId) {
          await http.post(
            `/admin/impersonation/${safeLogId}/stop`,
            {},
            authHeaders
          );
        } else {
          await http.post("/admin/impersonation/cleanup", {}, authHeaders);
        }
      } catch (e) {
        // Silently fail - token may already be invalid
      }

      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(LOG_ID_KEY);
      localStorage.removeItem(ADMIN_URL_KEY);
      localStorage.removeItem("impersonation_admin_name");
      commit("clearImpersonation");
    },

    setImpersonationFromUserResponse({ commit }, impersonationData) {
      if (impersonationData && impersonationData.is_impersonating) {
        commit("setImpersonation", {
          isImpersonating: true,
          adminName: impersonationData.admin_name,
          logId: impersonationData.log_id,
        });
      }
    },

    async fetchImpersonationLogs(_, params = {}) {
      const { data } = await http.get("/gatekeeper/impersonation/logs", {
        params,
      });
      return data;
    },
  },
};
